import { useState } from 'react';
import { MARKET_PROMOTION_PERMISSIONS } from '@/constants/permissions';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { donationBudgetsInfiniteQueryOptions } from '../api/marketPromotionP2.api';
import { DonationBudgetFormDialog } from '../components/DonationBudgetFormDialog';
import { getDonationBudgetColumns } from '../components/MpP2Columns';
import { useDonationBudgetSearch } from '../hooks/useDonationBudgetSearch';
import type { DonationBudget } from '../types/marketPromotionP2.types';
import { getBudgetStatCards } from '../utils/budgetStatCards';

export function DonationBudgetPage() {
  const controller = useDonationBudgetSearch();
  const { search } = controller;

  const [editing, setEditing] = useState<DonationBudget | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);

  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(donationBudgetsInfiniteQueryOptions(search));

  const columns = getDonationBudgetColumns(
    {
      onEdit: (row) => {
        setEditing(row);
        setFormOpen(true);
      },
    },
    search.search,
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Market Promotion Budget Entry"
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: 'Name, HR ID, mobile or item',
        }}
        createAction={{
          label: 'Add Budget',
          onClick: () => {
            setEditing(null);
            setFormOpen(true);
          },
          requires: MARKET_PROMOTION_PERMISSIONS.BUDGET_CREATE,
        }}
      />

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the budgets"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid isLoading={isLoading} stats={getBudgetStatCards(firstPageMeta?.totals)} />

          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            className="lg:flex-1"
            stickyColumnId="member"
            isRowHighlighted={(budget) => budget.id === editing?.id}
            emptyMessage="No budget matches"
            {...loadMoreProps}
          />
        </>
      )}

      {isFormOpen ? (
        <DonationBudgetFormDialog budget={editing} onClose={() => setFormOpen(false)} />
      ) : null}
    </div>
  );
}
